import React, { useEffect, useState } from "react";
import { FaLink, FaCheck, FaCamera, FaUser, FaLock, FaRegClock } from "react-icons/fa";
import { HiOutlinePencilSquare } from "react-icons/hi2";
import { useDispatch, useSelector } from "react-redux";
import { GetUser, uploadUserFile } from "../../../../Redux/Slices/User";
import Resume from "./Resume";
import PasswordReset from "./PasswordReset";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { BeatLoader } from "react-spinners";
import {
  FacebookShareButton,
  FacebookIcon,
  TwitterShareButton,
  TwitterIcon,
  LinkedinShareButton,
  LinkedinIcon,
  WhatsappShareButton,
  WhatsappIcon,
  TelegramShareButton,
  TelegramIcon,
} from "react-share";
import { getProfilePageUrl } from "../../../../Helper/shareUrl";
import avatar from "../../../../asset/new/images/avatar-1577909_1280.png";
import MaleAvatar from "../../../../asset/new/images/male.png";
import FemaleAvatar from "../../../../asset/new/images/female.png";
import "./JobseekerProfileDashboard.css";

const getCompletionTone = (pct) => {
  if (pct < 30) return "is-low";
  if (pct < 50) return "is-mid";
  if (pct < 80) return "is-good";
  return "is-high";
};

const Jobseekerprofile = () => {
  const { userData, completionPercentage, loading } = useSelector(
    (state) => state?.User
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const id = localStorage.getItem("id");
  const [copied, setCopied] = useState(false);
  const [image, setImage] = useState(null);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const timestamp = userData?.updated_at;
  const date = new Date(timestamp);
  const formattedDate = Number.isNaN(date.getTime())
    ? "Not updated yet"
    : date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });

  const firstName = (userData?.name || "").trim().split(" ")[0] || "there";
  const profileShareUrl = id ? getProfilePageUrl(id) : "";
  const shareTitle = userData?.name
    ? `${userData.name} | CAJobPortal Profile`
    : "CAJobPortal Profile";
  const pct = Number(completionPercentage) || 0;

  const displayValue = (value) => {
    if (value === null || value === undefined || `${value}`.trim() === "") {
      return null;
    }
    return value;
  };

  const getPlaceholderImage = () => {
    if (userData?.gender === "M" || userData?.gender === "Male") return MaleAvatar;
    if (userData?.gender === "F" || userData?.gender === "Female") return FemaleAvatar;
    return avatar;
  };

  useEffect(() => {
    if (!image) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(image);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [image]);

  const profileSource =
    previewUrl ||
    (userData?.profile_img && !imageLoadError
      ? userData.profile_img
      : getPlaceholderImage());

  const handleCopyLink = async () => {
    if (!profileShareUrl) return;
    try {
      await navigator.clipboard.writeText(profileShareUrl);
      setCopied(true);
      toast.success("Profile link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy link");
    }
  };

  const openFileInput = () => {
    document.getElementById("jsp-avatar-input")?.click();
  };

  const handleImageUpload = async () => {
    if (!image) {
      toast.error("Add an image first");
      return;
    }
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("profile_img", image);
      await uploadUserFile(id, formData);
      dispatch(GetUser(id));
      setImage(null);
    } finally {
      setUploadingImage(false);
    }
  };

  useEffect(() => {
    dispatch(GetUser(id));
    document.title = "Profile | CAJobPortal";
  }, [dispatch, id]);

  return (
    <div className="jsp-page">
      <div className="jsp-shell">
        <header className="jsp-intro">
          <p className="jsp-greeting">Welcome, {firstName}</p>
          <h1 className="jsp-title">Your Profile</h1>
          <p className="jsp-subtitle">
            Keep your profile complete so employers can find you faster.
          </p>
        </header>

        {/* Hero */}
        <section className="jsp-hero">
          <div className="jsp-avatar-wrap">
            <img
              src={profileSource}
              alt={userData?.name || "Profile"}
              className="jsp-avatar"
              onClick={openFileInput}
              onError={() => setImageLoadError(true)}
            />
            <button
              type="button"
              className="jsp-avatar-edit"
              onClick={openFileInput}
              aria-label="Change profile photo"
            >
              <FaCamera size={13} />
            </button>
            <input
              type="file"
              id="jsp-avatar-input"
              style={{ display: "none" }}
              accept="image/*"
              onChange={(e) => {
                setImageLoadError(false);
                setImage(e.target.files?.[0] || null);
              }}
            />
          </div>

          <div className="jsp-hero-body">
            <div className="jsp-hero-top">
              <div>
                <h2 className="jsp-name">{userData?.name || "Job Seeker"}</h2>
                <p className="jsp-meta">
                  <FaRegClock size={12} />
                  Last updated {formattedDate}
                </p>
              </div>
              <div className="jsp-hero-actions">
                <button
                  type="button"
                  className="jsp-btn jsp-btn-primary"
                  onClick={() => navigate("/jobseeker/profile/details")}
                >
                  <HiOutlinePencilSquare size={16} />
                  Edit Profile
                </button>
                {profileShareUrl && (
                  <a
                    className="jsp-btn jsp-btn-secondary"
                    href={profileShareUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Public Profile
                  </a>
                )}
              </div>
            </div>

            <div className="jsp-completion">
              <div className="jsp-completion-label">
                <span>Profile completion</span>
                <span className="jsp-completion-pct">{pct}%</span>
              </div>
              <div className="jsp-completion-track">
                <div
                  className={`jsp-completion-fill ${getCompletionTone(pct)}`}
                  style={{ width: `${Math.min(pct, 100)}%` }}
                />
              </div>
            </div>

            {image && (
              <div className="jsp-upload-pending">
                <span>{image.name}</span>
                <button
                  type="button"
                  className="jsp-btn jsp-btn-primary"
                  onClick={handleImageUpload}
                  disabled={uploadingImage}
                >
                  {uploadingImage ? "Uploading..." : "Upload Photo"}
                </button>
                <button
                  type="button"
                  className="jsp-btn jsp-btn-ghost"
                  onClick={() => setImage(null)}
                >
                  Cancel
                </button>
              </div>
            )}

            {loading && (
              <div className="jsp-loader">
                <BeatLoader color="#4893B3" size={10} />
              </div>
            )}
          </div>
        </section>

        <div className="jsp-grid">
          <div className="jsp-stack">
            {/* Basic Details */}
            <section className="jsp-card">
              <div className="jsp-card-head">
                <div className="jsp-card-head-left">
                  <div className="jsp-card-icon">
                    <FaUser />
                  </div>
                  <div>
                    <h3 className="jsp-card-title">Basic Details</h3>
                    <p className="jsp-card-desc">Visible on your public profile</p>
                  </div>
                </div>
                <button
                  type="button"
                  className="jsp-btn jsp-btn-ghost"
                  onClick={() => navigate("/jobseeker/profile/details")}
                  aria-label="Edit basic details"
                >
                  <HiOutlinePencilSquare size={18} />
                </button>
              </div>
              <div className="jsp-card-body">
                <div className="jsp-fields">
                  <div className="jsp-field">
                    <div className="jsp-field-label">Name</div>
                    <div className="jsp-field-value">
                      {displayValue(userData?.name) || (
                        <span className="jsp-field-empty">Not provided</span>
                      )}
                    </div>
                  </div>
                  <div className="jsp-field">
                    <div className="jsp-field-label">Email</div>
                    <div className="jsp-field-value">
                      {displayValue(userData?.email) || (
                        <span className="jsp-field-empty">Not provided</span>
                      )}
                    </div>
                  </div>
                  <div className="jsp-field">
                    <div className="jsp-field-label">Phone</div>
                    <div className="jsp-field-value">
                      {displayValue(userData?.mobile) || (
                        <span className="jsp-field-empty">Not provided</span>
                      )}
                    </div>
                  </div>
                  <div className="jsp-field">
                    <div className="jsp-field-label">Experience</div>
                    <div className="jsp-field-value">
                      {displayValue(userData?.experience_level) || (
                        <span className="jsp-field-empty">Not provided</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <Resume userData={userData} />
          </div>

          <div className="jsp-stack">
            {/* Share */}
            <section className="jsp-card">
              <div className="jsp-card-head">
                <div className="jsp-card-head-left">
                  <div className="jsp-card-icon">
                    <FaLink />
                  </div>
                  <div>
                    <h3 className="jsp-card-title">Share Profile</h3>
                    <p className="jsp-card-desc">Copy link or share socially</p>
                  </div>
                </div>
              </div>
              <div className="jsp-card-body">
                <div className="jsp-share-row">
                  <input
                    type="text"
                    className="jsp-share-input"
                    value={profileShareUrl}
                    readOnly
                    aria-label="Public profile link"
                    onFocus={(e) => e.target.select()}
                  />
                  <button
                    type="button"
                    className="jsp-btn jsp-btn-primary"
                    onClick={handleCopyLink}
                  >
                    {copied ? <FaCheck /> : <FaLink />}
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
                {profileShareUrl && (
                  <div className="jsp-social">
                    <WhatsappShareButton url={profileShareUrl} title={shareTitle}>
                      <WhatsappIcon size={36} round />
                    </WhatsappShareButton>
                    <LinkedinShareButton url={profileShareUrl} title={shareTitle}>
                      <LinkedinIcon size={36} round />
                    </LinkedinShareButton>
                    <FacebookShareButton url={profileShareUrl}>
                      <FacebookIcon size={36} round />
                    </FacebookShareButton>
                    <TwitterShareButton url={profileShareUrl} title={shareTitle}>
                      <TwitterIcon size={36} round />
                    </TwitterShareButton>
                    <TelegramShareButton url={profileShareUrl} title={shareTitle}>
                      <TelegramIcon size={36} round />
                    </TelegramShareButton>
                  </div>
                )}
              </div>
            </section>

            {/* Password */}
            <section className="jsp-card">
              <div className="jsp-card-head">
                <div className="jsp-card-head-left">
                  <div className="jsp-card-icon">
                    <FaLock />
                  </div>
                  <div>
                    <h3 className="jsp-card-title">Security</h3>
                    <p className="jsp-card-desc">Update your password</p>
                  </div>
                </div>
              </div>
              <div className="jsp-card-body">
                <PasswordReset />
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Jobseekerprofile;
